import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

/** CRM → Costes (solo admin). Todo en USD tal como lo da AWS; la vista lo pasa
 *  a € con `cambio.usd_eur`, que es el cambio de la última factura de AWS. */

export interface ServicioMes {
  servicio: string;
  nombre: string;
  que_es: string;
  importe: number;
}

export interface Mes {
  mes: string; // AAAA-MM
  uso: number;
  creditos: number; // negativo
  impuestos: number;
  neto: number;
  servicios: ServicioMes[];
}

export interface Cargo {
  periodo: string;
  entidad: string;
  emitida: string;
  subtotal_usd: number;
  creditos_usd: number;
  base_usd: number;
  iva_usd: number;
  total_usd: number;
  iva_pct: number;
  total_eur?: number;
  principal_id: string;
  documentos: { id: string; etiqueta: string }[];
  en_libro: boolean;
}

export interface Prevision {
  fin_de_mes: number;
  mes_tipo: number; // un mes normal: sin los gastos puntuales de este
  ritmo_diario: number;
  dias_que_quedan: number;
  confianza: 'alta' | 'media' | 'baja';
  base_mes_anterior: boolean;
}

export interface Plan {
  tipo: 'FREE' | 'PAID' | string;
  estado: string;
  creditos_restantes: number;
  caduca: string | null;
}

export interface Presupuesto {
  limite: number;
  gastado: number;
  previsto: number;
  avisos: { tipo: 'real' | 'previsto'; umbral_pct: number; a: string[] }[];
}

export interface Alarma {
  alarma: string;
  que: string;
  en_alarma: boolean;
}

export interface UsoIa {
  total: { estudios: number; agencia: number; coste_usd: number };
  este_mes: { estudios: number; agencia: number; coste_usd: number };
  por_resultado: Record<string, number>;
  coste_medio_lectura_usd: number;
  coste_100_estudios_usd: number;
  lecturas_estimadas: number;
  por_cliente: { client_id: string | null; nombre: string; estudios: number; coste_usd: number }[];
  clientes_con_cuenta: number;
}

export interface Escenario {
  clave: string;
  nombre: string;
  min: number;
  max: number;
  detalle: string;
}

export interface EventoCalendario {
  fecha: string;
  que: string;
  detalle: string;
  aviso: boolean;
}

export interface Costes {
  leido_en: number; // epoch s: cuándo se consultó AWS por última vez
  errores: Record<string, string>;
  cambio: { usd_eur: number; de_factura: boolean };
  meses: Mes[];
  mes_actual: Mes | null;
  prevision: Prevision | null;
  facturas: Cargo[];
  plan: Plan | null;
  presupuesto: Presupuesto | null;
  cif: { registrado: boolean } | null;
  almacenamiento: { bytes: number | null; objetos: number | null } | null;
  alarmas: Alarma[];
  ia: UsoIa;
  coste_por_cliente: { mes: string; usd: number; clientes: number } | null;
  escenarios: Escenario[];
  calendario: EventoCalendario[];
}

export function useCostes() {
  return useQuery<Costes>({
    queryKey: ['costes'],
    // La primera lectura del día consulta AWS y tarda unos segundos.
    queryFn: async () => (await api.get('/crm/costes', { timeout: 30000 })).data,
    staleTime: 5 * 60 * 1000,
  });
}

/** Fuerza una lectura nueva de AWS. El backend lo limita a una por hora. */
export function useActualizarCostes() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await api.post('/crm/costes/actualizar', null, { timeout: 30000 })).data as Costes,
    onSuccess: (data) => qc.setQueryData(['costes'], data),
  });
}

/** AWS no da el PDF, da un enlace firmado de ~15 minutos: se pide y se abre.
 *  La pestaña se abre ANTES de la petición para que el navegador no la trate
 *  como un pop-up. */
export async function abrirPdfFactura(invoiceId: string) {
  const pestana = window.open('', '_blank');
  try {
    const { data } = await api.get(`/crm/costes/facturas/${encodeURIComponent(invoiceId)}/pdf`);
    if (pestana) pestana.location.href = data.url;
    else window.location.href = data.url;
  } catch (e) {
    pestana?.close();
    throw e;
  }
}

export function useLlevarAlLibro() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (invoiceId: string) =>
      (await api.post(`/crm/costes/facturas/${encodeURIComponent(invoiceId)}/libro`)).data,
    onSettled: () => {
      qc.invalidateQueries({ queryKey: ['costes'] });
      qc.invalidateQueries({ queryKey: ['accounting'] });
    },
  });
}
